import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { CUSTOMERS_QUERY, VEHICLES_QUERY } from './contracts.gql';

interface CustomerOption {
        customerId: string;
        firmName: string;
        categoryName?: string | null;
        categoryLimit?: number | null;
        contactName?: string | null;
        contactEmail?: string | null;
        contactPhone?: string | null;
}

interface VehicleOption {
        vehicleId: string;
        plateNumber: string;
        make: string;
        model: string;
        color: string;
        defaultRfidTag: string;
}

interface CustomersQueryResponse {
        customers?: {
                nodes?: Array<{
                        customerId: string;
                        firmName?: string | null;
                        email?: string | null;
                        customerCategory?: {
                                description?: string | null;
                                simultaneousVehicleLimit?: number | null;
                        } | null;
                }> | null;
        } | null;
}

interface VehiclesQueryResponse {
        vehicles?: {
                nodes?: Array<{
                        vehicleId: string;
                        plateNumber?: string | null;
                        make?: string | null;
                        model?: string | null;
                        color?: string | null;
                }> | null;
        } | null;
}

export const useCustomerOptions = () => {
        const { data, loading, error } = useQuery<CustomersQueryResponse>(CUSTOMERS_QUERY, {
                fetchPolicy: 'cache-and-network',
                errorPolicy: 'all'
        });

        const customerOptions = useMemo<CustomerOption[]>(() => {
                if (!data?.customers?.nodes) {
                        return [];
                }

                return data.customers.nodes
                        .filter((node): node is NonNullable<typeof node> => Boolean(node))
                        .map(node => ({
                                customerId: node.customerId,
                                firmName: node.firmName ?? 'Unnamed Customer',
                                categoryName: node.customerCategory?.description ?? null,
                                categoryLimit: node.customerCategory?.simultaneousVehicleLimit ?? null,
                                contactName: null, // Not available from server
                                contactEmail: node.email ?? null,
                                contactPhone: null // Not available from server
                        }));
        }, [data?.customers?.nodes]);

        return {
                customerOptions,
                loading,
                error
        };
};

export const useVehicleOptions = () => {
        const { data, loading, error } = useQuery<VehiclesQueryResponse>(VEHICLES_QUERY, {
                fetchPolicy: 'cache-and-network',
                errorPolicy: 'all'
        });

        const vehicleOptions = useMemo<VehicleOption[]>(() => {
                if (!data?.vehicles?.nodes) {
                        return [];
                }

                return data.vehicles.nodes
                        .filter((node): node is NonNullable<typeof node> => Boolean(node))
                        .map(node => ({
                                vehicleId: node.vehicleId,
                                plateNumber: node.plateNumber ?? 'Unknown',
                                make: node.make ?? 'Unknown',
                                model: node.model ?? 'Unknown',
                                color: node.color ?? 'Unknown',
                                defaultRfidTag: '' // No RFID data available from server
                        }));
        }, [data?.vehicles?.nodes]);

        return {
                vehicleOptions,
                loading,
                error
        };
};
